import React, { createContext, useContext, useState, useMemo, type ReactNode } from 'react';

import type { Answer } from './types';
import { useAnswerList, AnswerListAction } from './hooks/useAnswerList';
import { useConversationId } from './hooks/useConversationId';

interface ChatSearchContextProps {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  queryId: string;
  setQueryId: React.Dispatch<React.SetStateAction<string>>;
  followUpQuery: string;
  setFollowUpQuery: React.Dispatch<React.SetStateAction<string>>;
  isSearching: boolean;
  setIsSearching: React.Dispatch<React.SetStateAction<boolean>>;
  showResults: boolean;
  setShowResults: React.Dispatch<React.SetStateAction<boolean>>;
  answers: Answer[];
  answerListDispatch: React.Dispatch<AnswerListAction>;
  activeAnswerIndex: number;
  setActiveAnswerIndex: React.Dispatch<React.SetStateAction<number>>;
  conversationId: string | undefined;
  setConversationId: (id: string) => void;
  clearConversationId: () => void;
  isLoadingConversation: boolean;
  setIsLoadingConversation: React.Dispatch<React.SetStateAction<boolean>>;
  loadError: string;
  setLoadError: React.Dispatch<React.SetStateAction<string>>;
  showMobileCloseWidgetButton: boolean;
  setShowMobileCloseWidgetButton: React.Dispatch<React.SetStateAction<boolean>>;
  contentTypeEndpoint?: string;
}

const ChatSearchContext = createContext<ChatSearchContextProps | undefined>(undefined);

export const ChatSearchProvider: React.FC<{
  children: ReactNode;
  searchPath?: string;
  contentTypeEndpoint?: string;
  widgetKey?: string;
}> = ({ children, searchPath, contentTypeEndpoint, widgetKey }) => {
  const [query, setQuery] = useState('');
  const [queryId, setQueryId] = useState('');
  const [followUpQuery, setFollowUpQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [answers, answerListDispatch] = useAnswerList();
  const [activeAnswerIndex, setActiveAnswerIndex] = useState(0);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [loadError, setLoadError] = useState<string>('');
  const [showMobileCloseWidgetButton, setShowMobileCloseWidgetButton] = useState(false);

  const { conversationId, setConversationId, clearConversationId } = useConversationId(searchPath, widgetKey);

  // Memoize the context value to prevent unnecessary re-renders of components that consume the context
  const contextValue = useMemo(
    () => ({
      query,
      setQuery,
      queryId,
      setQueryId,
      followUpQuery,
      setFollowUpQuery,
      isSearching,
      setIsSearching,
      showResults,
      setShowResults,
      answers,
      answerListDispatch,
      activeAnswerIndex,
      setActiveAnswerIndex,
      conversationId,
      setConversationId,
      clearConversationId,
      isLoadingConversation,
      setIsLoadingConversation,
      loadError,
      setLoadError,
      showMobileCloseWidgetButton,
      setShowMobileCloseWidgetButton,
      contentTypeEndpoint,
    }),
    [
      query,
      queryId,
      followUpQuery,
      isSearching,
      showResults,
      answers,
      answerListDispatch,
      activeAnswerIndex,
      conversationId,
      setConversationId,
      clearConversationId,
      isLoadingConversation,
      loadError,
      showMobileCloseWidgetButton,
      contentTypeEndpoint,
    ],
  );

  return <ChatSearchContext.Provider value={contextValue}>{children}</ChatSearchContext.Provider>;
};

export const useChatSearch = () => {
  const context = useContext(ChatSearchContext);
  if (!context) {
    throw new Error('useChatSearch must be used within a ChatSearchProvider');
  }

  return context;
};
